/**
 * Google Analytics, through Firebase.
 *
 * What the school wanted to know: how many families reach the portal, on what devices, and how
 * well it holds up when a whole campus re-enrols in the same week. GA4 collects the device,
 * browser and connection by itself; what it cannot work out on its own is which screen somebody
 * is on, because this is a single page that never reloads, and how long that screen took.
 *
 * Three rules this module keeps, in order of how much they matter:
 *
 * 1. **No family's details ever leave.** Only the path is sent, never the query string — a
 *    re-enrolment URL can carry a session or a parent code, and Google has no business holding
 *    either. There is no user id, and no event here takes a name, an address or an amount.
 * 2. **It can never break the portal.** Every entry point swallows its own errors. A blocked
 *    script, a browser that refuses analytics, a network that drops it — none of that should
 *    stop a parent paying their fees.
 * 3. **It stays out of the way.** The SDK is loaded on demand, so a family who never gets past
 *    the sign-in never downloads it, and nothing is measured until the app is idle.
 */

/** Public by design — this is the client-side config and it ships in the page either way. */
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

/**
 * Which environment this is, sent with everything.
 *
 * Dev, uat and production share one Firebase project, so without this our own testing would sit
 * in the same numbers as the families' and quietly inflate them. With it, production can be
 * looked at on its own.
 */
const environment = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'unknown';

/**
 * Off unless it has been configured and not switched off.
 *
 * A build with no measurement id — a developer's machine, or a deploy where it was left out —
 * does nothing at all rather than half-initialising and reporting to nowhere.
 */
export const isAnalyticsConfigured = () => Boolean(
    firebaseConfig.measurementId
    && firebaseConfig.apiKey
    && firebaseConfig.appId
    && String(process.env.REACT_APP_ANALYTICS_ENABLED).toLowerCase() !== 'false'
);

let ready = null;

/**
 * Loads the SDK once, the first time anything is measured.
 *
 * Imported here rather than at the top of the file so it lands in its own chunk: a family who
 * signs in and stops has downloaded none of it.
 */
const analytics = () => {
    if (!isAnalyticsConfigured()) return Promise.resolve(null);

    if (!ready) {
        ready = (async () => {
            const [{ initializeApp }, { getAnalytics, isSupported, setUserProperties }] = await Promise.all([
                import('firebase/app'),
                import('firebase/analytics'),
            ]);

            // Some browsers and privacy modes have no support for it at all, and calling
            // getAnalytics there throws rather than returning nothing.
            if (!await isSupported()) return null;

            const instance = getAnalytics(initializeApp(firebaseConfig));
            setUserProperties(instance, { environment });
            return instance;
        })().catch((error) => {
            // Blocked by an extension, offline, or refused. Worth one line in the console and
            // nothing more; it must not reach the person using the page.
            console.warn('Analytics did not start:', error?.message || error);
            return null;
        });
    }

    return ready;
};

/** Records an event, or quietly does not. Never throws, never returns a rejected promise. */
export const track = async (name, params = {}) => {
    try {
        const instance = await analytics();
        if (!instance) return;

        const { logEvent } = await import('firebase/analytics');
        logEvent(instance, name, { ...params, environment });
    } catch (error) {
        console.warn(`Analytics event "${name}" was not recorded:`, error?.message || error);
    }
};

/**
 * One screen view.
 *
 * The path only. `location.search` is dropped deliberately: several screens carry a session id
 * or a parent code in the query string, and sending those to Google would be handing over a
 * pointer to a family's re-enrolment.
 */
export const trackScreen = (pathname, title = document?.title) => track('screen_view', {
    firebase_screen: pathname,
    firebase_screen_class: pathname,
    page_title: title,
    page_path: pathname,
});

/**
 * How long the page took to become usable, once.
 *
 * From the Navigation Timing entry the browser has already recorded, so nothing is timed by us
 * and nothing is slowed down by measuring. Reported in milliseconds, rounded — a tenth of a
 * millisecond is noise, and a precise number reads as though it meant something.
 */
export const trackPageLoad = () => {
    try {
        const [timing] = performance?.getEntriesByType?.('navigation') || [];
        if (!timing) return;

        // A tab restored from the back/forward cache reports a load time of nearly nothing,
        // which would drag the average towards a speed nobody experienced.
        if (timing.type === 'back_forward') return;

        track('page_load', {
            load_ms: Math.round(timing.duration),
            dom_interactive_ms: Math.round(timing.domInteractive),
            first_byte_ms: Math.round(timing.responseStart),
            // GA4 reports the device itself; this says how the page was reached.
            navigation_type: timing.type,
        });
    } catch (error) {
        console.warn('Page load timing was not recorded:', error?.message || error);
    }
};
